import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchableSelect } from "./searchable-select";
import { FormDialog, Field } from "./form-dialog";
import {
  nameField,
  isoDateNotPast,
  timeField,
  durationField,
  notesField,
  lookupValue,
} from "@/lib/validators";

const schema = z.object({
  title: nameField(120),
  client: lookupValue("Pick a client"),
  location: nameField(160),
  date: isoDateNotPast,
  callTime: timeField.refine((v) => v >= "04:00", "Too early (min 04:00)"),
  duration: durationField,
  status: z.enum(["scheduled", "active", "wrapped", "post"]),
  notes: notesField,
});
type Values = z.infer<typeof schema>;

export function NewShootDialog({ label = "New shoot" }: { label?: string }) {
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: "", client: "", location: "", date: "", callTime: "09:00",
      duration: "4h", status: "scheduled", notes: "",
    },
  });
  const { register, handleSubmit, formState: { errors, isDirty }, setValue, watch, reset } = form;
  const [clientLabel, setClientLabel] = useState("");

  return (
    <FormDialog
      trigger={
        <Button size="sm" className="gap-1.5">
          <Plus className="size-3.5" /> {label}
        </Button>
      }
      title="Schedule a shoot"
      description="Add a production to the calendar. You can edit details later."
      submitLabel="Schedule shoot"
      isDirty={isDirty}
      onReset={() => { reset(); setClientLabel(""); }}
      onSubmit={() =>
        new Promise<void>((resolve, reject) => {
          handleSubmit(
            (v) => {
              // TODO: POST /api/shoots
              toast.success("Shoot scheduled", { description: v.title });
              resolve();
            },
            () => reject(),
          )();
        })
      }
    >
      <Field label="Title" htmlFor="shoot-title" error={errors.title?.message}>
        <Input id="shoot-title" placeholder="Editorial — VOGUE Japan" {...register("title")} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Client" error={errors.client?.message}>
          <SearchableSelect
            resource="clients"
            value={watch("client")}
            selectedLabel={clientLabel}
            onChange={(v, lbl) => {
              setValue("client", v, { shouldValidate: true, shouldDirty: true });
              setClientLabel(lbl);
            }}
            placeholder="Select client"
            searchPlaceholder="Search clients…"
          />
        </Field>
        <Field label="Status" error={errors.status?.message}>
          <Select value={watch("status")} onValueChange={(v) => setValue("status", v as Values["status"])}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="wrapped">Wrapped</SelectItem>
              <SelectItem value="post">Post</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>
      <Field label="Location" htmlFor="shoot-loc" error={errors.location?.message}>
        <Input id="shoot-loc" placeholder="Studio A · Pier 54" {...register("location")} />
      </Field>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Date" htmlFor="shoot-date" error={errors.date?.message}>
          <Input id="shoot-date" type="date" {...register("date")} />
        </Field>
        <Field label="Call time" htmlFor="shoot-call" error={errors.callTime?.message}>
          <Input id="shoot-call" type="time" {...register("callTime")} />
        </Field>
        <Field label="Duration" htmlFor="shoot-dur" error={errors.duration?.message}>
          <Input id="shoot-dur" placeholder="4h" {...register("duration")} />
        </Field>
      </div>
      <Field label="Notes" htmlFor="shoot-notes" error={errors.notes?.message}>
        <Textarea id="shoot-notes" rows={3} placeholder="Optional brief, looks, references…" {...register("notes")} />
      </Field>
    </FormDialog>
  );
}
